/**
 * Time, stored so that it survives contact with reality.
 *
 * Every timestamp carries the IANA zone it was captured in, not just an
 * instant. See ADR-0006: this app answers time-of-day questions, and an instant
 * alone does not preserve the wall-clock reading — it *recomputes* it from
 * whatever zone the device is in later. An entry logged at 08:00 in London in
 * January reads as 09:00 after the March clock change; a fortnight logged
 * abroad shifts wholesale on the flight home. Nothing errors, the timeline is
 * just quietly wrong, and the original reading is unrecoverable because it was
 * never written down.
 *
 * `epochMs` orders entries and measures durations. `timeZone` reconstructs what
 * the clock on the wall actually said.
 */

import { err, invalid, ok, type Result } from './result.js'

export interface Timestamp {
  /** The unambiguous instant. Use this for ordering and elapsed time. */
  readonly epochMs: number
  /** IANA identifier in effect at capture, e.g. `Europe/London`. */
  readonly timeZone: string
}

/** A clock reading, with no instant attached. */
export interface WallClock {
  readonly year: number
  /** 1–12, not the 0–11 that `Date` uses. */
  readonly month: number
  readonly day: number
  /** 0–23. */
  readonly hour: number
  readonly minute: number
  readonly second: number
}

/**
 * `Intl.DateTimeFormat` construction is expensive and this runs per entry when
 * grouping a few thousand of them, so formatters are reused.
 */
const formatters = new Map<string, Intl.DateTimeFormat>()

function formatterFor(timeZone: string): Intl.DateTimeFormat {
  const cached = formatters.get(timeZone)
  if (cached !== undefined) {
    return cached
  }
  const created = new Intl.DateTimeFormat('en-US', {
    timeZone,
    // h23 rather than hour12:false — the latter can yield hour 24 on some engines.
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  })
  formatters.set(timeZone, created)
  return created
}

export function isValidTimeZone(timeZone: string): boolean {
  try {
    formatterFor(timeZone)
    return true
  } catch {
    return false
  }
}

/**
 * Two zone strings can name the same zone.
 *
 * IANA keeps deprecated aliases alive, and engines disagree about which name to
 * report: Chromium's ICU returns `Asia/Calcutta` where Node returns
 * `Asia/Kolkata`. Both resolve to identical offsets, so every calculation here
 * is unaffected — but two entries recorded on different platforms can carry
 * different strings for one zone.
 *
 * The consequence, and it is the only one: **never group, filter or compare by
 * the zone string.** Compare resolved offsets, or wall-clock readings, or day
 * keys. Nothing in core does otherwise, and nothing should start.
 */
export function haveSameZoneRules(a: string, b: string, atEpochMs = 0): boolean {
  return a === b || offsetMinutesAt(atEpochMs, a) === offsetMinutesAt(atEpochMs, b)
}

/** Builds a timestamp, rejecting an unusable instant or an unknown zone. */
export function makeTimestamp(epochMs: number, timeZone: string): Result<Timestamp> {
  const errors = []
  if (!Number.isFinite(epochMs)) {
    errors.push(invalid('timestamp.instant.invalid', 'Not a valid instant.', 'epochMs'))
  }
  if (!isValidTimeZone(timeZone)) {
    errors.push(
      invalid(
        'timestamp.zone.unknown',
        `"${timeZone}" is not a recognised time zone.`,
        'timeZone',
      ),
    )
  }
  return errors.length > 0
    ? err<Timestamp>(...errors)
    : ok({ epochMs: Math.trunc(epochMs), timeZone })
}

/** The clock reading at an instant, in a given zone. */
export function wallClockIn(epochMs: number, timeZone: string): WallClock {
  const parts = formatterFor(timeZone).formatToParts(new Date(epochMs))
  const read = (type: Intl.DateTimeFormatPartTypes): number => {
    const part = parts.find((p) => p.type === type)
    if (part === undefined) {
      throw new Error(`Intl returned no "${type}" part for zone ${timeZone}`)
    }
    return Number(part.value)
  }
  return {
    year: read('year'),
    month: read('month'),
    day: read('day'),
    hour: read('hour'),
    minute: read('minute'),
    second: read('second'),
  }
}

/** The clock reading as originally recorded. This is the point of ADR-0006. */
export function wallClockOf(timestamp: Timestamp): WallClock {
  return wallClockIn(timestamp.epochMs, timestamp.timeZone)
}

/** Zone offset in minutes at a given instant, east of UTC positive. */
export function offsetMinutesAt(epochMs: number, timeZone: string): number {
  const wall = wallClockIn(epochMs, timeZone)
  const asIfUtc = Date.UTC(
    wall.year,
    wall.month - 1,
    wall.day,
    wall.hour,
    wall.minute,
    wall.second,
  )
  // Truncate to whole seconds: the wall clock carries no milliseconds.
  const instantSeconds = Math.floor(epochMs / 1000) * 1000
  return Math.round((asIfUtc - instantSeconds) / 60_000)
}

/**
 * The inverse of `wallClockIn`: which instant showed this reading in this zone?
 *
 * Two passes, because the offset depends on the instant we are still solving
 * for. The first guess uses the offset at the naive UTC interpretation; if that
 * lands on the other side of a DST transition the offset changes, and the
 * second pass corrects it. Times that do not exist (the spring-forward gap)
 * resolve to the instant immediately after the jump.
 */
export function epochFromWallClock(wall: WallClock, timeZone: string): number {
  const naive = Date.UTC(
    wall.year,
    wall.month - 1,
    wall.day,
    wall.hour,
    wall.minute,
    wall.second,
  )
  const firstOffset = offsetMinutesAt(naive, timeZone)
  const firstGuess = naive - firstOffset * 60_000
  const secondOffset = offsetMinutesAt(firstGuess, timeZone)
  return secondOffset === firstOffset ? firstGuess : naive - secondOffset * 60_000
}

function pad(value: number, width = 2): string {
  return String(Math.abs(value)).padStart(width, '0')
}

/**
 * `YYYY-MM-DD` in the captured zone — the key entries are grouped by.
 *
 * Grouping on anything else puts a 23:30 local entry on the following day once
 * UTC has rolled over, which is wrong on the one axis this app exists to
 * measure.
 */
export function dayKey(timestamp: Timestamp): string {
  const wall = wallClockOf(timestamp)
  return `${pad(wall.year, 4)}-${pad(wall.month)}-${pad(wall.day)}`
}

/** Midnight beginning the captured day, as an instant. */
export function startOfDay(timestamp: Timestamp): number {
  const wall = wallClockOf(timestamp)
  return epochFromWallClock(
    { ...wall, hour: 0, minute: 0, second: 0 },
    timestamp.timeZone,
  )
}

/** The last instant of the captured day, inclusive. */
export function endOfDay(timestamp: Timestamp): number {
  const wall = wallClockOf(timestamp)
  return (
    epochFromWallClock(
      { ...wall, hour: 23, minute: 59, second: 59 },
      timestamp.timeZone,
    ) + 999
  )
}

/** Local time as minutes past midnight. The basis of time-of-day analysis. */
export function minutesSinceMidnight(timestamp: Timestamp): number {
  const wall = wallClockOf(timestamp)
  return wall.hour * 60 + wall.minute
}

/** ISO 8601 with the captured offset, e.g. `2026-07-28T08:00:00.000+01:00`. */
export function toIso8601(timestamp: Timestamp): string {
  const wall = wallClockOf(timestamp)
  const millis = ((timestamp.epochMs % 1000) + 1000) % 1000
  const offset = offsetMinutesAt(timestamp.epochMs, timestamp.timeZone)
  const sign = offset < 0 ? '-' : '+'
  const offsetText = `${sign}${pad(Math.trunc(offset / 60))}:${pad(offset % 60)}`
  return (
    `${pad(wall.year, 4)}-${pad(wall.month)}-${pad(wall.day)}` +
    `T${pad(wall.hour)}:${pad(wall.minute)}:${pad(wall.second)}.${pad(millis, 3)}` +
    offsetText
  )
}

/** Chronological ordering. Correct across zones by construction. */
export function compareTimestamps(a: Timestamp, b: Timestamp): number {
  return a.epochMs - b.epochMs
}

/** Elapsed milliseconds from `a` to `b`. Unaffected by zone or DST. */
export function durationBetween(a: Timestamp, b: Timestamp): number {
  return b.epochMs - a.epochMs
}

/** True when both fall on the same local calendar day in their own zones. */
export function isSameDay(a: Timestamp, b: Timestamp): boolean {
  return dayKey(a) === dayKey(b)
}
